import { FiCheck, FiCircle } from "react-icons/fi"
import type { Rule } from "antd/es/form"

// Backend-Regel (forgotPasswordConfirmSchema): 8 bis 128 Zeichen, mindestens ein Klein- und ein Großbuchstabe und eine Ziffer
export const PASSWORD_MIN_LENGTH = 8
export const PASSWORD_MAX_LENGTH = 128

export type TPasswordRequirement = {
    id: string
    label: string
    test: (password: string) => boolean
}

export const PASSWORD_REQUIREMENTS: TPasswordRequirement[] = [
    {
        id: "length",
        label: `Mindestens ${PASSWORD_MIN_LENGTH} Zeichen`,
        test: (password) => password.length >= PASSWORD_MIN_LENGTH,
    },
    {
        id: "lowercase",
        label: "Ein Kleinbuchstabe",
        test: (password) => /[a-z]/.test(password),
    },
    {
        id: "uppercase",
        label: "Ein Großbuchstabe",
        test: (password) => /[A-Z]/.test(password),
    },
    {
        id: "digit",
        label: "Eine Ziffer",
        test: (password) => /\d/.test(password),
    },
]

export const PASSWORD_MATCH_LABEL = "Passwörter stimmen überein"

export const passwordsMatch = (password: string, confirmPassword: string) => confirmPassword.length > 0 && password === confirmPassword

export const passwordMeetsRequirements = (password: string) =>
    password.length <= PASSWORD_MAX_LENGTH && PASSWORD_REQUIREMENTS.every((requirement) => requirement.test(password))

/** antd-Regeln für das Passwortfeld (wie in der Zentrale). */
export const passwordFieldRules: Rule[] = [
    { required: true, message: "Bitte ein Passwort eingeben" },
    {
        validator: (_, value: unknown) => {
            const password = typeof value === "string" ? value : ""
            if (!password) return Promise.resolve()
            if (password.length > PASSWORD_MAX_LENGTH)
                return Promise.reject(new Error(`Das Passwort darf höchstens ${PASSWORD_MAX_LENGTH} Zeichen lang sein.`))
            if (!passwordMeetsRequirements(password)) return Promise.reject(new Error("Bitte erfüllen Sie alle Passwort-Anforderungen."))
            return Promise.resolve()
        },
    },
]

/** antd-Regeln für das Wiederholungsfeld; `passwordField` = Name des Passwortfelds im Formular. */
export const passwordConfirmFieldRules = (passwordField = "password"): Rule[] => [
    { required: true, message: "Bitte das Passwort wiederholen" },
    ({ getFieldValue }) => ({
        validator: (_, value) =>
            !value || getFieldValue(passwordField) === value ? Promise.resolve() : Promise.reject(new Error("Passwörter stimmen nicht überein.")),
    }),
]

type PasswordRequirementsListProps = {
    password?: string
    confirmPassword?: string
    emphasizeUnmet?: boolean
}

function RequirementRow(props: { label: string; met: boolean; emphasizeUnmet?: boolean }) {
    const tone = props.met ? "text-green-600" : props.emphasizeUnmet ? "text-red-600" : "text-gray-500"
    return (
        <li className={`flex items-center gap-2 text-sm leading-5 ${tone}`}>
            {props.met ? <FiCheck className="h-4 w-4 shrink-0" aria-hidden /> : <FiCircle className="h-3.5 w-3.5 shrink-0" aria-hidden />}
            <span>
                <span className="sr-only">{props.met ? "Erfüllt: " : "Offen: "}</span>
                {props.label}
            </span>
        </li>
    )
}

/** Live-Checkliste der Passwort-Anforderungen (wie basis-frontend-webapp). */
export default function PasswordRequirementsList(props: PasswordRequirementsListProps) {
    const password = props.password ?? ""
    const showMatch = props.confirmPassword !== undefined
    const exceedsMaxLength = password.length > PASSWORD_MAX_LENGTH

    return (
        <div className="mb-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
            <p className="mb-2 text-xs font-medium text-gray-500">Passwort-Anforderungen</p>
            <ul className="flex flex-col gap-1.5" aria-label="Passwort-Anforderungen">
                {PASSWORD_REQUIREMENTS.map((requirement) => (
                    <RequirementRow
                        key={requirement.id}
                        label={requirement.label}
                        met={requirement.test(password)}
                        emphasizeUnmet={props.emphasizeUnmet}
                    />
                ))}
                {exceedsMaxLength && <RequirementRow label={`Höchstens ${PASSWORD_MAX_LENGTH} Zeichen`} met={false} emphasizeUnmet />}
                {showMatch && (
                    <RequirementRow
                        label={PASSWORD_MATCH_LABEL}
                        met={passwordsMatch(password, props.confirmPassword ?? "")}
                        emphasizeUnmet={props.emphasizeUnmet}
                    />
                )}
            </ul>
        </div>
    )
}
